import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";
import "../css/ArticleTab.css";
import SimpleMDE from "react-simplemde-editor";
import "easymde/dist/easymde.min.css";
import slugify from "slugify";

const ArticleTab = ({ user }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("News & Updates");
  const [tags, setTags] = useState(["Blog"]);
  const [displayName, setDisplayName] = useState("");
  const [originalDisplayName, setOriginalDisplayName] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [ogImageUrl, setOgImageUrl] = useState("");

  const MAX_TAGS = 5;

  const tagCategories = {
    "🔥 Game Versions & Settings": [
      "GTA6",
      "GTA5",
      "GTAOnline",
      "ViceCity",
      "SanAndreas",
      "LibertyCity",
      "LosSantos",
    ],
    "📚 Content Type": [
      "News",
      "Updates",
      "Guides",
      "Rankings",
      "Tutorials",
      "Mods",
      "Leaks",
      "Reviews",
      "EasterEggs",
      "Walkthroughs",
      "Comparison",
    ],
    "🎨 Community & Creativity": [
      "FanArt",
      "Memes",
      "Cosplay",
      "FanFiction",
      "CommunityEvents",
      "Screenshots",
    ],
    "🧠 Story & Lore": [
      "Lore",
      "Theories",
      "Timeline",
      "Characters",
      "Protagonists",
      "Lucia",
      "Jason",
      "Michael",
      "Trevor",
      "Franklin",
      "CJ",
      "TommyVercetti",
      "NikoBellic",
    ],
    "🎮 Gameplay Features": [
      "Roleplay",
      "Heists",
      "Cars",
      "Weapons",
      "Houses",
      "Missions",
      "Police",
      "Map",
      "Customizations",
    ],
    "🌐 Online & Events": [
      "OnlineEvents",
      "GTAOnline2",
      "Multiplayer",
      "RPServers",
      "Streamers",
    ],
  };

  useEffect(() => {
    const fetchPublicProfile = async () => {
      try {
        const publicRef = doc(db, "public_profiles", user.uid);
        const publicSnap = await getDoc(publicRef);
        if (publicSnap.exists()) {
          const data = publicSnap.data();
          setDisplayName(data.displayName || "");
          setOriginalDisplayName(data.displayName || "");
        }
      } catch (error) {
        console.error("Failed to load public profile:", error);
      } finally {
      }
    };

    if (user?.uid) {
      fetchPublicProfile();
    }
  }, [user]);

    // Auto-generate slug when title changes (once or when slug empty)
  useEffect(() => {
    if (!slug && title) {
      setSlug(
        slugify(title, { lower: true, strict: true, trim: true }).slice(0, 80)
      );
    }
  }, [title]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !content) {
      setMessage("Please fill in both fields.");
      return;
    }
    try {
      await addDoc(collection(db, "articles"), {
        title,
        content,
        category,
        tags,
        authorName: displayName || "Anonymous",
        slug: slug || slugify(title, { lower: true, strict: true }),
        metaTitle: metaTitle || `${title} — GTAFanPage`,
        metaDescription:
          metaDescription ||
          content.replace(/[#*_>`]/g, "").slice(0, 155), // simple fallback
        ogImageUrl: ogImageUrl || "",
        views: 0,
        uid: user.uid,
        timestamp: serverTimestamp(),
        updatedAt: serverTimestamp(),
        published: true, // optional
      });

      setTitle("");
      setContent("");
      setMetaTitle("");
      setMetaDescription("");
      setSlug("");
      setOgImageUrl("");
      setMessage("✅ Article submitted successfully!");
    } catch (err) {
      console.error(err);
      setMessage("❌ Failed to submit article.");
    }
  };

  return (
    <div className="article-tab">
      <h2>Write a New Article (react-markdown)</h2>
      <form onSubmit={handleSubmit} className="article-form">
        <div className="article-columns">
          {/* Left Column */}
            <div className="left-column">
            <input
              type="text"
              placeholder="Enter article title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <SimpleMDE value={content} onChange={setContent} />

            {/* --- SEO fields --- */}
            <input
              type="text"
              placeholder="SEO Title (optional)"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
            />
            <input
              type="text"
              placeholder="SEO Description (150–160 chars)"
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              maxLength={160}
            />
            <input
              type="text"
              placeholder="Slug (auto-generated)"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
            <input
              type="url"
              placeholder="OG Image URL (optional)"
              value={ogImageUrl}
              onChange={(e) => setOgImageUrl(e.target.value)}
            />

            <button type="submit">Submit Article</button>
          </div>

          {/* Right Column */}
          <div className="right-column">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Select Category</option>
              <option value="News & Updates">News & Updates</option>
              <option value="Guides & Tutorials">Guides & Tutorials</option>
              <option value="Lore & Theories">Lore & Theories</option>
              <option value="Ranked Lists & Opinions">
                Ranked Lists & Opinions
              </option>
              <option value="Fan Content & Creativity">
                Fan Content & Creativity
              </option>
              <option value="Game Reviews & Retrospectives">
                Game Reviews & Retrospectives
              </option>
              <option value="GTA RP Coverage">GTA RP Coverage</option>
              <option value="Community Spotlight">Community Spotlight</option>
              <option value="Mods & Technical Help">
                Mods & Technical Help
              </option>
              <option value="Opinion & Editorials">Opinion & Editorials</option>
            </select>
            {/* Selected Tags */}
            {tags.length > 0 && (
              <div className="selected-tags">
                <p>Selected Tags:</p>
                <div className="selected-tag-list">
                  {tags.map((tag) => (
                    <span key={tag} className="selected-tag">
                      #{tag}
                      <button
                        type="button"
                        className="remove-tag-button"
                        onClick={() =>
                          setTags((prev) => prev.filter((t) => t !== tag))
                        }
                        title="Remove tag"
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="tag-section">
              <p>Select up to 5 tags:</p>
              {Object.entries(tagCategories).map(([category, tagsArray]) => (
                <div key={category} className="tag-category">
                  <h4>{category}</h4>
                  <div className="tag-options">
                    {tagsArray.map((tag) => {
                      const isSelected = tags.includes(tag);
                      const isDisabled = !isSelected && tags.length >= MAX_TAGS;
                      return (
                        <button
                          key={tag}
                          type="button"
                          className={`tag-button ${
                            isSelected ? "selected" : ""
                          }`}
                          disabled={isDisabled}
                          onClick={() => {
                            setTags((prev) =>
                              isSelected
                                ? prev.filter((t) => t !== tag)
                                : [...prev, tag]
                            );
                          }}
                        >
                          #{tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </form>
      {message && <p className="article-message">{message}</p>}
    </div>
  );
};

export default ArticleTab;
